import React, { useRef } from 'react'
import { Button, Menu, Typography, ButtonMenu } from '@ann/web-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../../../../../../hooks';
import { HeaderActions, HeaderContainer, HeaderInfo } from './DataAccessHeader.styled';

const DataAccessHeader = ({ onClickAdd, showMenu, onUploadToAgent, onBrowseDrives, onCloseMenu, menuRef }) => {
    const navigate = useNavigate();
    const { agentId } = useParams();
    const fileInputRef = useRef(null);
    const { fileStore, authStore, documentStore, playgroundConfigStore, playgroundAgentStore } = useStore();

    const handleUploadToAgent = () => {
        // Close the menu using the specific function
        onCloseMenu();

        // Trigger file input click
        fileInputRef.current?.click();
    };


    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (file) {
            // Mark that a real file is being uploaded
            sessionStorage.setItem('isUploadingFile', 'true');

            console.log(' Starting file upload:', file.name);
            documentStore.pushDocument({ name: file.name, state: 'loading' }); // Add temporal document
            console.log(' Temporary document added with state: loading');

            // Navigate immediately to DocumentsView to show the temporary card
            navigate(`documents?from=upload&filename=${encodeURIComponent(file.name)}`);

            // Start the actual upload in the background
            try {
                await uploadFileInBackground(file);
                // Clear the flag after completing the upload
                sessionStorage.removeItem('isUploadingFile');
            } catch (error) {
                console.error('Error uploading file in background:', error);
                // Clear the flag in case of error
                sessionStorage.removeItem('isUploadingFile');
            }
        }
        // Clear the input
        event.target.value = '';
    };

    const uploadFileInBackground = async (file) => {
        try {
            const maxSize = 100 * 1024 * 1024; // 100MB
            if (file.size > maxSize) {
                console.error('File too large');
                return;
            }

            if (!authStore.currentWorkspaceUuid || (!authStore.apiUserId && !authStore.userId)) {
                console.error('Authentication data incomplete');
                return;
            }

            // Get user ID from token (apiUserId) or fallback to Cognito userId
            const userId = authStore.apiUserId || authStore.userId;

            // 1. Create document first
            const createDocumentBody = {
                name: file.name,
                vault_uuid: playgroundConfigStore.agentStore.vaultUuid,
                created_by: userId,
                owner: userId,
                tenant_id: "58145bef-4f71-468a-bded-1823d2a08f58",
                type: file.type || 'application/octet-stream',
                url: '', // Will be updated after upload
                metadata: {
                    source: "file_upload",
                    version: "1.0"
                }
            };

            const createDocumentResponse = await fileStore.createDocument(createDocumentBody);

            // 2. Assign document to agent
            const assignDocumentToAgentBody = {
                document_uuid: createDocumentResponse.uuid,
                agent_uuid: agentId,
                expires_at: "2026-12-31 23:59:59",
                owner: userId,
                created_by: userId,
                granted_by: userId,
            };

            await playgroundAgentStore.assignDocumentAgent(assignDocumentToAgentBody);

            // 3. Create file with the new parameters
            const createFileBody = {
                name: file.name,
                folder_id: `folder-${Date.now()}`,
                tenant_id: "58145bef-4f71-468a-bded-1823d2a08f58",
                user_id: userId,
                file_type: file.type || 'application/octet-stream',
                status: 'active',
                vault_uuid: `87101387-bb0e-48e4-8863-8fba43953636`, // vaultUuid is the uuid of the created document
                document_uuid: createDocumentResponse.uuid,
                version_uuid: createDocumentResponse.versionUuid
            };

            const createFileResponse = await fileStore.createFile(createFileBody);

            if (createFileResponse?.body?.uploadUrl) {
                const uploadUrl = createFileResponse.body.uploadUrl;
                const fileUuid = createFileResponse.body.file_uuid;

                // Upload file
                const uploadResponse = await fetch(uploadUrl, {
                    method: 'PUT', 
                    body: file,
                    headers: {
                        'Content-Type': file.type || 'application/octet-stream',
                    }
                });


                if (uploadResponse.ok) {
                    // 4. Attach document
                    const attachDocumentBody = {
                        file_uuid: fileUuid,
                        documentUuid: createDocumentResponse.uuid,
                        tenantId: "58145bef-4f71-468a-bded-1823d2a08f58"
                    };

                    await fileStore.attachDocument(attachDocumentBody);
                    console.log('File uploaded successfully in background:', file.name);
                    console.log('Updating document state to: active');
                    documentStore.updateDocumentStateByName(file.name, 'active'); // Update document state to active
                    console.log('Document state update complete');
                } else {
                    throw new Error(`Upload failed: ${uploadResponse.status}`);
                }
            } else {
                throw new Error('No upload URL received');
            }
        } catch (error) {
            console.error('Background upload failed:', error);
        }
    };

    return (
        <HeaderContainer>
            <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                accept="*/*"
            />

            <HeaderInfo>
                <Typography category="subtitle" size="medium" weight="bold" color="title">
                    Data Access
                </Typography>
                <Typography category="body" size="medium" weight="regular" color="body">
                    Choose what information your agent can use to answer questions.
                </Typography>
            </HeaderInfo>
            <HeaderActions>
                <ButtonMenu
                    customIcon="plus"
                    iconPosition="left"
                    label="Add"
                    menuPosition="bottom"
                    onChange={(value) => {
                        if (value === 'upload-to-agent') {
                            handleUploadToAgent();
                        } else if (value === 'browser-drives') {
                            onBrowseDrives();
                        }
                    }}
                    size="medium"
                    type="secondary"
                    width="260px"
                >
                    <ButtonMenu.Menu>
                        <ButtonMenu.Item
                            label="Upload Knowledge"
                            value="upload-to-agent"
                        />
                        <ButtonMenu.Item
                            label="Add from Drives"
                            value="browser-drives"
                        />
                    </ButtonMenu.Menu>
                </ButtonMenu>
            </HeaderActions>
        </HeaderContainer>
    )
}

export default DataAccessHeader
